import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getQueue, dequeue, incrementRetry, initQueue, type QueuedAction } from '@/lib/offlineQueue';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook that processes the offline queue when the app comes back online.
 * Should be mounted once at the app level.
 */
export function useOfflineQueueProcessor() {
  const { toast } = useToast();
  const [processing, setProcessing] = useState(false);

  // Initialize IDB queue on mount
  useEffect(() => {
    initQueue();
  }, []);

  const processQueue = useCallback(async () => {
    const queue = getQueue();
    if (queue.length === 0 || processing) return;

    setProcessing(true);
    let successCount = 0;
    let failCount = 0;

    for (const action of queue) {
      try {
        await processAction(action);
        await dequeue(action.id);
        successCount++;
      } catch {
        if (action.retryCount >= 3) {
          await dequeue(action.id);
          failCount++;
        } else {
          await incrementRetry(action.id);
          failCount++;
        }
      }
    }

    setProcessing(false);

    if (successCount > 0) {
      toast({ title: `${successCount} handling${successCount > 1 ? 'er' : ''} synkroniseret` });
    }
    if (failCount > 0) {
      toast({ title: `${failCount} handling${failCount > 1 ? 'er' : ''} fejlede`, variant: 'destructive' });
    }
  }, [processing, toast]);

  useEffect(() => {
    const handleOnline = () => {
      setTimeout(processQueue, 1000);
    };
    window.addEventListener('online', handleOnline);

    if (navigator.onLine && getQueue().length > 0) {
      processQueue();
    }

    return () => window.removeEventListener('online', handleOnline);
  }, [processQueue]);

  return { processing, processQueue };
}

async function processAction(action: QueuedAction) {
  const { type, payload } = action;

  switch (type) {
    case 'check_in': {
      const { taskId, timestamp, status } = payload;
      const { error } = await supabase.from('service_tickets')
        .update({ check_in_at: timestamp, status } as any)
        .eq('id', taskId);
      if (error) throw error;
      break;
    }
    case 'check_out': {
      const { taskId, timestamp, status, accumulated_minutes } = payload;
      const { error } = await supabase.from('service_tickets')
        .update({ check_out_at: timestamp, status, accumulated_minutes } as any)
        .eq('id', taskId);
      if (error) throw error;
      break;
    }
    case 'pause': {
      const { taskId, status, pause_reason, accumulated_minutes } = payload;
      const { error } = await supabase.from('service_tickets')
        .update({ status, pause_reason, accumulated_minutes, check_in_at: null } as any)
        .eq('id', taskId);
      if (error) throw error;
      break;
    }
    case 'resume': {
      const { taskId, timestamp, status } = payload;
      const { error } = await supabase.from('service_tickets')
        .update({ status, check_in_at: timestamp, pause_reason: null } as any)
        .eq('id', taskId);
      if (error) throw error;
      break;
    }
    case 'room_complete': {
      const { roomId, notes } = payload;
      const { error } = await supabase.from('rooms')
        .update({ is_complete: true, notes: notes || null })
        .eq('id', roomId);
      if (error) throw error;
      break;
    }
    case 'save_notes': {
      const { roomId, notes } = payload;
      const { error } = await supabase.from('rooms')
        .update({ notes: notes || null })
        .eq('id', roomId);
      if (error) throw error;
      break;
    }
    case 'audit_log': {
      const { company_id, user_id, entity_type, entity_id, action: auditAction, metadata } = payload;
      const { error } = await supabase.from('activity_logs').insert({
        company_id, user_id, entity_type, entity_id, action: auditAction, metadata,
      });
      if (error) throw error;
      break;
    }
    case 'deviation': {
      const { insert_data } = payload;
      const { error } = await supabase.from('extra_work_requests').insert(insert_data as any);
      if (error) throw error;
      break;
    }
    case 'update_ticket_status': {
      const { taskId, status } = payload;
      const { error } = await supabase.from('service_tickets')
        .update({ status } as any)
        .eq('id', taskId);
      if (error) throw error;
      break;
    }
    case 'create_comment': {
      const { ticket_id, user_id, content } = payload;
      const { error } = await supabase.from('ticket_comments')
        .insert({ ticket_id, user_id, content } as any);
      if (error) throw error;
      break;
    }
    case 'update_schedule_entry': {
      const { entryId, updates } = payload;
      const { error } = await supabase.from('schedule_entries')
        .update(updates as any)
        .eq('id', entryId);
      if (error) throw error;
      break;
    }
    default:
      console.warn('Unknown offline action type:', type);
  }
}
